import React, { useEffect, useState } from "react";

import {
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { ArrowDown, ArrowUp } from "react-feather";
import { ExpandLess, ExpandMore } from "@mui/icons-material";

function flattenTree(nodes: any[]): any[] {
  return nodes.reduce(
    (acc, node) => [...acc, node, ...flattenTree(node.children ?? [])],
    []
  );
}

function mergeComparative(current: any[], previous: any[]) {
  const prevById = new Map(flattenTree(previous).map((r) => [r.id, r]));
  const addPrev = (node: any): any => {
    const prev = prevById.get(node.id);
    // yhdistä tilit code:n mukaan
    const accounts = (node.accounts ?? []).map((acc: any) => {
      const prevAcc = prev?.accounts?.find((pa: any) => pa.code === acc.code);
      return { ...acc, valuePrev: prevAcc?.value ?? null };
    });
    const children = (node.children ?? []).map(addPrev);

    return {
      ...node,
      valuePrev: prev?.value ?? null,
      accounts,
      children,
    };
  };
  return current.map(addPrev);
}

function renderTableRows(
  data: any[],
  level = 0,
  expandedRows: any[],
  handleToggleExpand: (id: string) => void
) {
  return data.map((item) => (
    <React.Fragment key={item.id}>
      <TableRow>
        <TableCell style={{ paddingLeft: `${level * 20}px` }}>
          {item.accounts ? (
            <IconButton
              size="small"
              onClick={() => handleToggleExpand(item.id)}
            >
              {expandedRows.includes(item.id) ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          ) : (
            <span style={{ paddingLeft: 20 }} />
          )}
          {item.name}
        </TableCell>
        <TableCell align="right">
          {item.value != null
            ? new Intl.NumberFormat("fi-FI", {
                style: "currency",
                currency: "EUR",
              }).format(item.value)
            : "-"}
        </TableCell>
        <TableCell align="right">
          {item.valuePrev != null
            ? new Intl.NumberFormat("fi-FI", {
                style: "currency",
                currency: "EUR",
              }).format(item.valuePrev)
            : "-"}
        </TableCell>
      </TableRow>

      {expandedRows.includes(item.id) &&
        (item.accounts ?? []).map((acc: any) => (
          <TableRow key={acc.code}>
            <TableCell style={{ paddingLeft: `${(level + 1) * 20}px` }}>
              {acc.code} – {acc.name}
            </TableCell>
            <TableCell align="right">
              {acc.value != null
                ? new Intl.NumberFormat("fi-FI", {
                    style: "currency",
                    currency: "EUR",
                  }).format(acc.value)
                : "-"}
            </TableCell>
            <TableCell align="right">
              {acc.valuePrev != null
                ? new Intl.NumberFormat("fi-FI", {
                    style: "currency",
                    currency: "EUR",
                  }).format(acc.valuePrev)
                : "-"}
            </TableCell>
          </TableRow>
        ))}

      {item.children &&
        renderTableRows(
          item.children,
          level + 1,
          expandedRows,
          handleToggleExpand
        )}
    </React.Fragment>
  ));
}

function nestData(data: any) {
  const map: any = {};
  const result: any[] = [];

  data.forEach((item: any) => (map[item.id] = { ...item, children: [] }));
  data.forEach((item: any) => {
    if (item.parentId) {
      map[item.parentId]?.children.push(map[item.id]);
    } else {
      result.push(map[item.id]);
    }
  });

  return result;
}

function IncomeStatement({
  rows,
  prevRows,
}: {
  rows: any[];
  prevRows?: any[];
}) {
  const [expandedRows, setExpandedRows] = useState<string[]>([]);
  const nestedCurrent = nestData(rows);
  const nestedPrevious = nestData(prevRows ?? []);
  const merged = mergeComparative(nestedCurrent, nestedPrevious);

  useEffect(() => {
    setExpandedRows(rows.map((r: any) => r.id));
  }, [rows]);

  const toggle = (id: string) =>
    setExpandedRows((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );

  return (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Nimi</TableCell>
            <TableCell align="right">Nykyinen</TableCell>
            <TableCell align="right">Edellinen</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {renderTableRows(merged, 0, expandedRows, toggle)}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default IncomeStatement;
